"use client";

import { useState } from "react";

interface ContactFormProps {
  copy: {
    nameLabel: string;
    namePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    messageLabel: string;
    messagePlaceholder: string;
    submitLabel: string;
  };
}

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormState {
  isSubmitting: boolean;
  isSuccess: boolean;
  error: string;
}

const inputBase =
  "mt-3 block w-full border-b border-[#D9D1C6] bg-transparent py-3.5 text-sm text-[#2B2824] placeholder:text-[#C8C0B3] transition-colors focus:border-[#2B2824] focus:outline-none";

const labelBase =
  "block text-[10px] font-semibold tracking-[0.22em] text-[#A09589] uppercase";

export function ContactForm({ copy }: ContactFormProps) {
  const [formData, setFormData] = useState<FormData>({ name: "", email: "", message: "" });
  const [formState, setFormState] = useState<FormState>({ isSubmitting: false, isSuccess: false, error: "" });

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formState.error) setFormState((prev) => ({ ...prev, error: "" }));
  }

  function validate(): string {
    if (!formData.name.trim()) return "Name is required";
    if (!formData.email.trim()) return "Email is required";
    if (!formData.message.trim()) return "Message is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return "Please enter a valid email address";
    return "";
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormState((prev) => ({ ...prev, error: "", isSuccess: false }));
    const err = validate();
    if (err) { setFormState((prev) => ({ ...prev, error: err })); return; }

    setFormState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send message");
      setFormData({ name: "", email: "", message: "" });
      setFormState({ isSubmitting: false, isSuccess: true, error: "" });
    } catch (err) {
      setFormState({
        isSubmitting: false,
        isSuccess: false,
        error: err instanceof Error ? err.message : "Failed to send message. Please try again.",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">

      {/* Error */}
      {formState.error && (
        <div className="flex items-start gap-3 border-l-2 border-red-300 bg-red-50 px-4 py-3">
          <p className="text-sm leading-6 text-red-700">{formState.error}</p>
        </div>
      )}

      {/* Success */}
      {formState.isSuccess && (
        <div className="flex items-start gap-3 border-l-2 border-[#B8AB96] bg-[#F5F0E8] px-4 py-3">
          <p className="text-sm leading-6 text-[#4F4A43]">
            Mensagem enviada com sucesso. Entraremos em contacto em breve.
          </p>
        </div>
      )}

      {/* Name + Email — side by side */}
      <div className="grid gap-8 sm:grid-cols-2">
        <label className={labelBase}>
          {copy.nameLabel || "Name"}
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={copy.namePlaceholder}
            autoComplete="name"
            required
            className={inputBase}
          />
        </label>

        <label className={labelBase}>
          {copy.emailLabel || "Email"}
          <input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={copy.emailPlaceholder}
            autoComplete="email"
            required
            className={inputBase}
          />
        </label>
      </div>

      {/* Message */}
      <label className={labelBase}>
        {copy.messageLabel || "Message"}
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={7}
          placeholder={copy.messagePlaceholder}
          required
          className={`${inputBase} resize-none`}
        />
      </label>

      {/* Divider + submit */}
      <div className="border-t border-[#D9D1C6] pt-8">
        <button
          type="submit"
          disabled={formState.isSubmitting}
          className="w-full bg-[#4F4A43] py-4 text-[11px] font-semibold tracking-[0.22em] text-[#F8F6F1] uppercase
                     transition hover:bg-[#2B2824] disabled:cursor-not-allowed disabled:opacity-50
                     sm:w-auto sm:px-12"
        >
          {formState.isSubmitting
            ? (copy.submitLabel ? `${copy.submitLabel}…` : "Sending…")
            : (copy.submitLabel || "Send message")}
        </button>
      </div>

    </form>
  );
}
